'use client';

/**
 * BrandingContext — platform-wide branding settings sourced from the backend.
 *
 * Endpoint (read, public — no auth required):
 *   GET /api/cms/platform-settings/
 *
 * Expected shape (all fields optional — defaults applied):
 * {
 *   app_name: string;
 *   app_name_ar: string;
 *   logo_url: string | null;       // full URL or relative path
 *   favicon_url: string | null;
 *   primary_color: string | null;  // hex, e.g. "#2563EB"
 *   secondary_color: string | null;
 *   homepage_banner_url: string | null;
 *   homepage_banner_title_ar: string | null;
 *   homepage_banner_subtitle_ar: string | null;
 * }
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

// ── Defaults (reflect the current hardcoded values) ─────────────────────────
export const BRANDING_DEFAULTS = {
  app_name: 'Maknna',
  app_name_ar: 'مكنة',
  logo_url: null as string | null,
  favicon_url: null as string | null,
  primary_color: null as string | null,   // null = use CSS variable
  secondary_color: null as string | null,
  homepage_banner_url: null as string | null,
  homepage_banner_title_ar: null as string | null,
  homepage_banner_subtitle_ar: null as string | null,
};

export type BrandingSettings = typeof BRANDING_DEFAULTS;

interface BrandingContextValue {
  branding: BrandingSettings;
  loading: boolean;
  refresh: () => void;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: BRANDING_DEFAULTS,
  loading: false,
  refresh: () => {},
});

const CACHE_KEY = 'maknna_branding_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function applyBrandingToDocument(b: BrandingSettings) {
  if (typeof document === 'undefined') return;

  // Dynamic CSS custom properties for primary / secondary colours
  if (b.primary_color) {
    // Convert hex to HSL for the Tailwind CSS variable format
    document.documentElement.style.setProperty('--branding-primary', b.primary_color);
  } else {
    document.documentElement.style.removeProperty('--branding-primary');
  }
  if (b.secondary_color) {
    document.documentElement.style.setProperty('--branding-secondary', b.secondary_color);
  } else {
    document.documentElement.style.removeProperty('--branding-secondary');
  }

  // Favicon
  if (b.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingSettings>(BRANDING_DEFAULTS);
  const [loading, setLoading] = useState(true);

  const fetchBranding = async () => {
    try {
      // Check cache first
      const cached = typeof window !== 'undefined' ? localStorage.getItem(CACHE_KEY) : null;
      if (cached) {
        const { data, ts } = JSON.parse(cached) as { data: BrandingSettings; ts: number };
        if (Date.now() - ts < CACHE_TTL_MS) {
          setBranding({ ...BRANDING_DEFAULTS, ...data });
          applyBrandingToDocument({ ...BRANDING_DEFAULTS, ...data });
          setLoading(false);
          return;
        }
      }
    } catch { /* ignore */ }

    try {
      const res = await fetch(`${API}/api/cms/platform-settings/`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const raw = await res.json();
        // The endpoint may return a list (first item) or a single object
        const data: Partial<BrandingSettings> = Array.isArray(raw) ? raw[0] ?? {} : raw;
        const merged = { ...BRANDING_DEFAULTS, ...data };
        setBranding(merged);
        applyBrandingToDocument(merged);
        if (typeof window !== 'undefined') {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: merged, ts: Date.now() }));
        }
      }
      // If endpoint doesn't exist yet (404/500) → keep defaults silently
    } catch { /* network error — keep defaults */ }
    finally { setLoading(false); }
  };

  const refresh = () => {
    if (typeof window !== 'undefined') localStorage.removeItem(CACHE_KEY);
    fetchBranding();
  };

  useEffect(() => { fetchBranding(); }, []);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
